import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useAddressdetailsdraftFetchMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { liveAddresses, mergeDrafts } from "./addressFields";

/* The Address & Contact section's data. */
export const useAddressContact = () => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  const [draftRows, setDraftRows] = useState([]);
  const [fetchDrafts] = useAddressdetailsdraftFetchMutation();

  const companyID = currentEditingCompany?.companyID;

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  const addresses = liveAddresses(currentEditingCompany);

  const loadDrafts = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await fetchDrafts({ companyID, token });
      /* Transcribed: the live code only sets state when the nested `data` is
         present, so a company with no drafts keeps the previous list — which
         is the empty one it started with. */
      if (res?.data?.data) {
        setDraftRows(mergeDrafts(res.data.data, liveAddresses(currentEditingCompany)));
      }
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. The section falls back to the company's own addresses,
         which is the safe reading: it shows what is live rather than nothing. */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token, currentEditingCompany]);

  useEffect(() => {
    if (!currentEditingCompany) return;
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany]);

  /* Transcribed: when drafts exist they REPLACE the live list, because
     mergeDrafts has already folded in any address without a draft. */
  const rows = draftRows.length > 0 ? draftRows : addresses.map((a) => a.company_address_detailsID);

  return {
    /* The records the cards render. */
    rows,
    /* Whether the section has nothing at all — the live AddButton condition,
       which checks BOTH lists. */
    isEmpty: draftRows.length <= 0 && addresses.length <= 0,
    /* The live cards read `primary_address` off the join row, not the detail,
       so it is looked up separately when opening the modal. */
    primaryFor: (record) =>
      addresses.find(
        (a) =>
          a?.company_address_detailsID?.company_address_detailsID ===
          record?.company_address_detailsID
      )?.primary_address,
    refresh: () => {
      refetch();
      loadDrafts();
    },
  };
};

export default useAddressContact;
